/**
 * Notification Component
 * 
 * Custom notification system without external dependencies
 *
 * @package DesignSchool\Forms
 */

declare var jQuery: any;

export type NotificationType = 'success' | 'error' | 'warning' | 'info';

interface NotificationOptions {
    title: string;
    message: string;
    type: NotificationType;
    duration?: number; // Auto-close duration in ms (0 = no auto-close)
    callback?: () => void;
}

export class Notification {
    private $: any;
    private $container: any;
    private notifications: Map<string, any> = new Map();
    private defaultDuration: number = 5000; // 5 seconds

    constructor() {
        this.$ = jQuery;
        
        // Initialize when DOM is ready
        if (this.$ && typeof this.$ === 'function') {
            this.$(document).ready(() => {
                this.init();
            });
            
            // Also try immediate init if DOM is already ready
            if (document.readyState === 'complete' || document.readyState === 'interactive') {
                this.init();
            }
        }
    }

    /**
     * Initialize notification container
     */
    private init(): void {
        // Ensure jQuery is available
        if (typeof this.$ === 'undefined' || !this.$) {
            console.error('[Notification] jQuery is not available');
            return;
        }

        // Create or get container
        this.$container = this.$('.ds-forms-notifications');
        
        if (!this.$container.length) {
            this.$container = this.$('<div>')
                .addClass('ds-forms-notifications')
                .appendTo(this.$('body'));
            
            console.log('[Notification] Container created');
        } else {
            console.log('[Notification] Container found');
        }
    }

    /**
     * Show success notification
     */
    public success(title: string, message: string, callback?: () => void): void {
        this.show({
            title,
            message,
            type: 'success',
            callback
        });
    }

    /**
     * Show error notification
     */
    public error(title: string, message: string, callback?: () => void): void {
        this.show({
            title,
            message,
            type: 'error',
            duration: 0, // Errors don't auto-close
            callback
        });
    }

    /**
     * Show warning notification
     */
    public warning(title: string, message: string, callback?: () => void): void {
        this.show({
            title,
            message,
            type: 'warning',
            callback
        });
    }

    /**
     * Show info notification
     */
    public info(title: string, message: string, callback?: () => void): void {
        this.show({
            title,
            message,
            type: 'info',
            callback
        });
    }

    /**
     * Show notification
     */
    private show(options: NotificationOptions): void {
        // Ensure container exists
        if (!this.$container || !this.$container.length) {
            this.init();
        }

        const id = 'notification-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9);
        const duration = options.duration !== undefined ? options.duration : this.defaultDuration;

        console.log('[Notification] Showing notification:', {
            type: options.type,
            title: options.title,
            message: options.message
        });

        // Create notification element
        const $notification = this.createNotification(id, options);

        // Add to container
        this.$container.append($notification);
        this.notifications.set(id, $notification);

        // Trigger show animation
        setTimeout(() => {
            $notification.addClass('ds-forms-notification-show');
        }, 10);

        // Auto-close if duration > 0
        if (duration > 0) {
            setTimeout(() => {
                this.close(id);
                // Call callback on auto-close too
                if (options.callback) {
                    options.callback();
                }
            }, duration);
        }

        // Trigger event
        this.$(document).trigger('ds-forms-notification-shown', [id, options]);
    }

    /**
     * Create notification element
     */
    private createNotification(id: string, options: NotificationOptions): any {
        const iconMap: Record<NotificationType, string> = {
            success: '✓',
            error: '✕',
            warning: '[!]',
            info: 'ℹ'
        };

        const $notification = this.$('<div>')
            .addClass('ds-forms-notification')
            .addClass('ds-forms-notification-' + options.type)
            .attr('data-notification-id', id);

        // Icon
        const $icon = this.$('<div>')
            .addClass('ds-forms-notification-icon')
            .text(iconMap[options.type]);

        // Content
        const $content = this.$('<div>')
            .addClass('ds-forms-notification-content');

        const $title = this.$('<div>')
            .addClass('ds-forms-notification-title')
            .text(options.title);

        const $message = this.$('<div>')
            .addClass('ds-forms-notification-message')
            .text(options.message);

        $content.append($title).append($message);

        // Close button
        const $close = this.$('<button>')
            .addClass('ds-forms-notification-close')
            .attr('type', 'button')
            .attr('aria-label', 'Close')
            .on('click', () => {
                this.close(id);
                if (options.callback) {
                    options.callback();
                }
            });

        $notification.append($icon).append($content).append($close);

        return $notification;
    }

    /**
     * Close notification
     */
    public close(id: string): void {
        const $notification = this.notifications.get(id);
        
        if (!$notification || !$notification.length) {
            return;
        }

        // Trigger hide animation
        $notification.addClass('ds-forms-notification-hide');

        // Remove after animation
        setTimeout(() => {
            $notification.remove();
            this.notifications.delete(id);
            this.$(document).trigger('ds-forms-notification-closed', [id]);
        }, 300);
    }

    /**
     * Close all notifications
     */
    public closeAll(): void {
        this.notifications.forEach((_, id) => {
            this.close(id);
        });
    }

    /**
     * Get notification count
     */
    public count(): number {
        return this.notifications.size;
    }
}

// Export singleton
export const notification = new Notification();

